// TableSortingHelper.js
// This file contains helper functions and components for implementing sortable tables

import React from 'react';
import { TableCell, TableSortLabel, Box } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * Creates a sortable table header cell
 * @param {string} id - The column ID (matches the data property name)
 * @param {string} label - The display label for the column
 * @param {object} sortConfig - The current sort configuration { key, direction }
 * @param {function} onRequestSort - Function to call when sorting is requested
 * @param {object} cellProps - Additional props to pass to the TableCell component
 * @returns {JSX.Element} A sortable table header cell
 */
export const SortableTableCell = ({ id, label, sortConfig, onRequestSort, ...cellProps }) => {
  const isActive = sortConfig.key === id;
  const direction = isActive ? sortConfig.direction : 'asc';
  
  return (
    <TableCell 
      {...cellProps}
      sortDirection={isActive ? direction : false}
      sx={{ 
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        ...cellProps.sx
      }}
      onClick={() => onRequestSort(id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {isActive && (
          <Box component="span" sx={{ ml: 0.5 }}>
            {direction === 'asc' ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

/**
 * Sort function for different data types
 * @param {array} array - The array to sort
 * @param {string} key - The property to sort by
 * @param {string} direction - The sort direction ('asc' or 'desc')
 * @returns {array} The sorted array
 */
export const sortData = (array, key, direction) => {
  if (!key) return array;
  
  return [...array].sort((a, b) => {
    // Handle different data types
    if (typeof a[key] === 'boolean') {
      // For boolean values (true values first for ascending)
      if (a[key] === b[key]) return 0;
      if (direction === 'asc') {
        return a[key] ? -1 : 1;
      } else {
        return a[key] ? 1 : -1;
      }
    } else if (typeof a[key] === 'number') {
      // For numeric values
      return direction === 'asc' 
        ? a[key] - b[key]
        : b[key] - a[key];
    } else {
      // For string values (case insensitive)
      const valueA = String(a[key] || '').toLowerCase();
      const valueB = String(b[key] || '').toLowerCase();
      
      if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
  });
};

/**
 * Hook for managing sorting state
 * @param {string} defaultKey - The default sort key
 * @param {string} defaultDirection - The default sort direction
 * @returns {array} [sortConfig, requestSort, sortedData]
 */
export const useSortableData = (data, defaultKey = null, defaultDirection = 'asc') => {
  const [sortConfig, setSortConfig] = React.useState({
    key: defaultKey,
    direction: defaultDirection
  });
  
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const sortedData = React.useMemo(() => {
    return sortData(data, sortConfig.key, sortConfig.direction);
  }, [data, sortConfig]);
  
  return [sortConfig, requestSort, sortedData];
};

export default {
  SortableTableCell,
  sortData,
  useSortableData
};
